import { FC, Key, useEffect, useState } from 'react';
import { isDesktop, isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ReactSVG } from 'react-svg';
import { Switch } from 'antd';
import cn from 'classnames';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { getGameBoxView } from '../../store/selectors';
import BonusCard from '../BonusCard';
import GameMap from '../GameMap';
import GameMapMobile from '../GameMapMobile';
import TeamsList from '../Teams';

import 'swiper/css';
import 'swiper/swiper-bundle.css';
import './styles.scss';

interface MissionsProps {
  data: any;
  onClose?: () => void;
  isLandscape?: boolean;
  mobileHeader?: HTMLDivElement | null;
  gameBoxWidgetVisibilityHandler?: (st?: boolean) => void;
}

const Missions: FC<MissionsProps> = ({ data, onClose, isLandscape, mobileHeader, gameBoxWidgetVisibilityHandler }) => {
  const { submissions } = data;
  const { t } = useTranslation();
  const gameBoxView = useSelector(getGameBoxView);
  const isWidget = gameBoxView === 'widget';
  const enableMap = ['money', 'welcome', 'other'].includes(data?.categories[0]);
  const submissionToStart = submissions.find(
      (submission: { status: string }) => submission.status === 'selectSportTeamToStart'
  );
  const submissionTeamInfo = submissions.find((sub: { teamInfo: Record<string, string> }) => sub.teamInfo)?.teamInfo;
  const missionClasses = cn('bonus__progressive-missions');
  const [swiperInstance, setSwiperInstance] = useState<any>(null);
  const [showGameMap, setShowGameMap] = useState<boolean>(false);
  const elementMobileBonus = document.querySelector('.my-bonuses_widget');
  const elementTitleAndDesc = document.querySelector(
      '.my-bonuses__cards > .game-box-widget__content--title-and-description'
  ) as HTMLElement;
  const screenHeight = window.innerHeight < 800;

  useEffect(() => {
    if (swiperInstance && submissions && submissions?.length > 0) {
      setShowGameMap(false);
      if (elementTitleAndDesc) {
        elementTitleAndDesc.style.display = 'block';
      }
      elementMobileBonus?.classList.remove('custom_scroll');
      const activeIndex = submissions.findIndex((mission: { status: string }) => mission.status === 'inProgress');
      swiperInstance.slideTo(activeIndex);
    }
  }, [submissions, swiperInstance]);

  const onChange = (checked: boolean) => {
    (screenHeight || isMobile) && elementMobileBonus?.classList.toggle('custom_scroll', checked);
    if (elementTitleAndDesc && screenHeight) {
      elementTitleAndDesc.style.display = checked ? 'none' : 'block';
    }
    setShowGameMap(checked);
  };

  useEffect(() => {
    if (!mobileHeader) return;

    mobileHeader.style.display = isLandscape && isMobile && showGameMap ? 'none' : 'flex';
  }, [isLandscape, showGameMap, mobileHeader]);

  return (
      <div className={missionClasses}>
        {enableMap && isWidget && submissions?.length > 2 && (
            <div
                className={cn('game-map', {
                  'game-map-landscape': isLandscape && isMobile && showGameMap,
                })}
            >
              <p>{t('game_map')}</p>
              <Switch
                  size={isMobile ? 'small' : 'default'}
                  className={showGameMap ? 'switch_on' : 'switch_off'}
                  checked={showGameMap}
                  onChange={onChange}
              />
            </div>
        )}
        {!showGameMap ? (
            <div className="bonus__progressive-missions__content" id={`progressive-missions${data?.id}`}>
              {(submissionToStart || (submissionTeamInfo && Object.keys(submissionTeamInfo)?.length > 0)) && isWidget && (
                  <TeamsList
                      missionId={submissionToStart?.mission_id}
                      progressiveMissionId={submissionToStart?.progressive_mission_id}
                      submissionTeamInfo={submissionTeamInfo}
                  />
              )}
              <div className="navigations">
                <div className="custom-swiper-button-prev">
                  <ReactSVG src="/static/images/chevron_left.svg" />
                </div>
                <div className="custom-swiper-button-next">
                  <ReactSVG src="/static/images/chevron_right.svg" />
                </div>
              </div>
              <Swiper
                  direction={'horizontal'}
                  slidesPerView={(isWidget && isDesktop) || (isLandscape && isMobile) ? 2.2 : 1.03}
                  spaceBetween={isLandscape && isMobile ? 15 : 5}
                  mousewheel={{ releaseOnEdges: true }}
                  modules={[Navigation]}
                  navigation={{
                    prevEl: '.custom-swiper-button-prev',
                    nextEl: '.custom-swiper-button-next',
                  }}
                  style={{
                    width: isWidget && isDesktop ? '898px' : isLandscape && isMobile && '730px',
                  }}
                  onSwiper={setSwiperInstance}
              >
                {(submissions && submissions?.length > 0 && submissions)?.map(
                    (mission: { id: Key; status: string }, index: number) => {
                      return (
                          <SwiperSlide key={mission?.id}>
                            <div className="bonus__card__holder">
                              <BonusCard data={mission} inGame={false} onClose={onClose} isWidget={isWidget} gameBoxWidgetVisibilityHandler={gameBoxWidgetVisibilityHandler} />
                              <ReactSVG
                                  src="/static/images/right-arrow.svg"
                                  className={cn('arrows', {
                                    'hide-arrow': index === submissions?.length - 1,
                                  })}
                              />
                            </div>
                          </SwiperSlide>
                      );
                    }
                )}
              </Swiper>
            </div>
        ) : isDesktop ? (
            <GameMap data={submissions} onClose={onClose} />
        ) : (
            <GameMapMobile data={submissions} onClose={onClose} isLandscape={isLandscape} />
        )}
      </div>
  );
};

export default Missions;
